import { createContext, useContext, useEffect, useState } from 'react';
import { StatusCode } from '../components/Alert.tsx';
import JournalsController from '../controllers/JournalsController.ts';
import FormatDate from '../models/helpers/formatDate.ts';
import { Journal } from '../models/journals/journal.ts';
import { JournalSummary } from '../models/journals/journalSummary.ts';
import { JournalSearchParam, JournalSummarySearchParam } from '../models/searchParams/journalSearchParam.ts';
import { TableSearchParam } from '../models/searchParams/tableSearchParam.ts';
import { useAlert } from './alertProvider.tsx';

type JournalContextType = {
    isLoaded?: boolean;
    setIsLoaded: (newIsLoaded: boolean) => void;
    days: JournalSummary[];
    groupedJournals: Journal[][];
    setGroupedJournals: (groups: Journal[][]) => void;
    searchParam: JournalSearchParam;
    setSearchParam: (param: JournalSearchParam) => void;
    insertJournals: (entities: Journal[]) => boolean;
    updateJournals: (entities: Journal[]) => boolean;
    deleteJournals: (entities: Journal[]) => boolean;
};

const JournalContext = createContext<JournalContextType>({} as JournalContextType);

export const JournalContextProvider = ({ children }: { children: any }) => {
    //#region getting dates
    const today = new Date();
    const defaultDateFrom = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const dayOfWeek = defaultDateFrom.getDay();

    const generateWeeksBefore = 4;
    const generateWeeksAfter = 1;

    // Calculate the previous Monday
    const daysToMonday = (dayOfWeek === 0 ? -6 : 1 - dayOfWeek); // If Sunday, go back 6 days; otherwise, go back to Monday
    const mondayBefore = new Date(defaultDateFrom);
    mondayBefore.setDate(defaultDateFrom.getDate() + daysToMonday);
    mondayBefore.setDate(mondayBefore.getDate() - (7 * generateWeeksBefore));

    // Calculate the next Sunday
    const daysToSunday = (dayOfWeek === 0 ? 0 : 7 - dayOfWeek); // If Sunday, it's the same day; otherwise, go forward to Sunday
    const sundayAfter = new Date(defaultDateFrom);
    sundayAfter.setDate(defaultDateFrom.getDate() + daysToSunday);
    sundayAfter.setDate(sundayAfter.getDate() + (7 * generateWeeksAfter));
    //#endregion
    const { showAlert } = useAlert();
    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const [isDaysLoaded, setIsDaysLoaded] = useState<boolean>(false);
    const [days, setDays] = useState<JournalSummary[]>([]);
    const [daysSearchParam, setDaysSearchParam] = useState<JournalSummarySearchParam>({ dateFrom: mondayBefore, dateTo: sundayAfter } as JournalSummarySearchParam);
    const [groupedJournals, setGroupedJournals] = useState<Journal[][]>([]);
    const [tableSearchParam, setTableSearchParam] = useState<TableSearchParam>({ offset: 0, limit: 25 });
    const [searchParam, setSearchParam] = useState<JournalSearchParam>({ dateTime: new Date(defaultDateFrom), sortExpression: 'journalID' } as JournalSearchParam);

    const fetchDays = async () => {
        try {
            const result = await JournalsController.getSummary(daysSearchParam);
            if (result.notificationStatus != StatusCode.success) {
                showAlert(result.notificationStatus, result.notificationMessage);
                return;
            }
            var days: JournalSummary[] = result.journalSummaries.map((x: any) => ({ percent: x.percent, dateTime: new Date(x.dateTime), calories: x.calories, userGoalCalories: x.userGoalCalories, protein: x.protein, carbohydrates: x.carbohydrates, totalFat: x.totalFat } as JournalSummary)).sort((a: JournalSummary, b: JournalSummary) => a.dateTime.getDate() - b.dateTime.getTime());

            const startDay: Date = days[0]?.dateTime < mondayBefore
                ? new Date(days[0]?.dateTime)
                : new Date(mondayBefore);
            const endDay: Date = days[days.length - 1]?.dateTime > sundayAfter
                ? new Date(days[days.length - 1]?.dateTime)
                : new Date(sundayAfter);

            var generatedDays: JournalSummary[] = [];
            for (var forDate: Date = new Date(startDay); forDate <= endDay; forDate.setDate(forDate.getDate() + 1)) {
                const existingDay = days.find((day) => FormatDate(day.dateTime) === FormatDate(forDate));
                if (existingDay) {
                    generatedDays.push(existingDay);
                } else {
                    generatedDays.push({ percent: 0, dateTime: new Date(forDate), calories: 0, protein: 0, carbohydrates: 0, totalFat: 0 } as JournalSummary);
                }
            }
            setDays(generatedDays);
            setIsDaysLoaded(true);
        } catch (error) {
            console.error('Error fetching journal summaries:', error);
        }
    }
    useEffect(() => {
        if (isDaysLoaded == false)
            fetchDays();
    }, [isDaysLoaded]);
    useEffect(() => {
        setIsDaysLoaded(false);
    }, [daysSearchParam])

    const fetchJournals = async () => {
        try {
            const result = await JournalsController.get(tableSearchParam, searchParam);
            if (result.notificationStatus != StatusCode.success) {
                showAlert(result.notificationStatus, result.notificationMessage);
                return;
            }
            if (result.hasMorePages == true) {
                var limit = tableSearchParam.limit * 2;
                setTableSearchParam({ offset: 0, limit: limit })
                return;
            }
            const newGroupedJournals = Object.values(
                result.journals.reduce((groups: Record<string, Journal[]>, journal: Journal) => {
                    const groupName = journal.name;
                    if (!groups[groupName]) {
                        groups[groupName] = [];
                    }
                    groups[groupName].push(journal);
                    return groups;
                }, {})
            );
            setGroupedJournals(newGroupedJournals as Journal[][]);
            setIsLoaded(true);
        } catch (error) {
            console.error('Error fetching journal records:', error);
        }
    };
    useEffect(() => {
        if (isLoaded == false)
            fetchJournals();
    }, [isLoaded]);
    useEffect(() => {
        setIsLoaded(false);
    }, [tableSearchParam, searchParam])

    const insertJournals = (entities: Journal[]) => {
        entities.forEach((entity) => {
            JournalsController.post(entity)
                .then(result => {
                    if (result.notificationStatus != StatusCode.success) {
                        showAlert(result.notificationStatus, result.notificationMessage);
                        return false;
                    }
                })
                .catch(error => {
                    console.error('Error inserting journal:', error);
                    return false;
                });
        });
        setIsLoaded(false);
        setIsDaysLoaded(false);
        return true;
    }

    const updateJournals = (entities: Journal[]) => {
        entities.forEach((entity) => {
            JournalsController.put(entity)
                .then(result => {
                    if (result.notificationStatus != StatusCode.success) {
                        showAlert(result.notificationStatus, result.notificationMessage);
                        return false;
                    }
                })
                .catch(error => {
                    console.error('Error updating journal:', error);
                    return false;
                });
        });
        setIsLoaded(false);
        setIsDaysLoaded(false);
        return true;
    }

    const deleteJournals = (entities: Journal[]) => {
        entities.forEach((entity) => {
            JournalsController.delete(entity.journalID)
                .then(result => {
                    if (result.notificationStatus != StatusCode.success) {
                        showAlert(result.notificationStatus, result.notificationMessage);
                        return false;
                    }
                })
                .catch(error => {
                    console.error('Error deleting journal:', error);
                    return false;
                });
        });
        setIsLoaded(false);
        setIsDaysLoaded(false);
        return true;
    }

    const context: JournalContextType = {
        isLoaded,
        setIsLoaded,
        days,
        groupedJournals,
        setGroupedJournals,
        searchParam,
        setSearchParam,
        insertJournals,
        updateJournals,
        deleteJournals
    }

    return (
        <JournalContext.Provider value={context}>
            {children}
        </JournalContext.Provider>
    )
}

export const useJournalContext = () => useContext(JournalContext)
